import { useCallback, useRef, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';
import { SyncReportsButton } from '@/components/SyncReportsButton';
import { SnapshotBarCompareCard } from '@/components/snapshot/SnapshotBarCompareCard';
import { snapshotRangesValid } from '@/components/snapshot/SnapshotDateRangePicker';
import { SnapshotGaugeCard } from '@/components/snapshot/SnapshotGaugeCard';
import { SnapshotKpiGrid } from '@/components/snapshot/SnapshotKpiGrid';
import { SnapshotRoasCard } from '@/components/snapshot/SnapshotRoasCard';
import { ProductReportSection } from '@/components/snapshot/ProductReportSection';
import { SnapshotSpendHero } from '@/components/snapshot/SnapshotSpendHero';
import { ConsolidatedDashboard } from '@/components/snapshot/ConsolidatedDashboard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { useWorkspaceRetailKpis } from '@/hooks/useWorkspaceRetailKpis';
import { adsApi } from '@/api/client';
import { dashboardPdfFilename, downloadDashboardPdf } from '@/api/dashboardPdfExport';
import { reportAdProductsQueryParam } from '@/api/reportAdProducts';
import {
  organicPaidFromSnapshot,
  organicPaidHasData,
  parseSnapshotPayload,
} from '@/api/snapshotMetrics';
import { isConsolidatedDashboardView } from '@/api/consolidatedMetrics';
import { EMPTY_RETAIL_KPI_METRICS } from '@/utils/workspaceAdProductMetrics';

export function SnapshotPage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const [searchParams] = useSearchParams();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const { user } = useAuth();
  const { showToast } = useToast();
  const isDemo = Boolean(user?.is_demo);
  const exportRootRef = useRef<HTMLDivElement>(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const { range: rangeState } = useAppDateRange();

  const adProductsParam = useMemo(() => reportAdProductsQueryParam([]), []);
  const compareRange = useMemo(
    () => ({ ...rangeState, compareEnabled: true }),
    [rangeState]
  );
  const rangesOk = snapshotRangesValid(compareRange);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'sp-report-overview',
      profileId,
      compareRange.current.startDate,
      compareRange.current.endDate,
      compareRange.previous.startDate,
      compareRange.previous.endDate,
      adProductsParam,
    ],
    queryFn: async () => {
      const res = await adsApi.getSpReportSnapshot(profileId, {
        startDate: compareRange.current.startDate,
        endDate: compareRange.current.endDate,
        compareStartDate: compareRange.previous.startDate,
        compareEndDate: compareRange.previous.endDate,
        adProducts: adProductsParam,
      });
      if (!res.success) throw new Error('Failed to load dashboard');
      return parseSnapshotPayload(res.data);
    },
    enabled: Boolean(profileId) && rangesOk,
  });

  const retailKpis = useWorkspaceRetailKpis(profileId, compareRange, {
    enabled: !isDemo && Boolean(profileId) && rangesOk,
  });

  const compareEnabled = true;
  const currentView = data
    ? organicPaidFromSnapshot(
        data.current,
        isDemo ? EMPTY_RETAIL_KPI_METRICS : retailKpis.current,
        { demo: isDemo }
      )
    : null;
  const previousView = data
    ? organicPaidFromSnapshot(
        data.previous,
        isDemo ? EMPTY_RETAIL_KPI_METRICS : retailKpis.previous,
        { demo: isDemo }
      )
    : null;

  const empty = Boolean(
    data &&
      currentView &&
      previousView &&
      !organicPaidHasData(currentView, data.current.rowCount) &&
      !organicPaidHasData(previousView, data.previous.rowCount)
  );

  const waitingOnRetail = !isDemo && retailKpis.isLoading;
  const showLoading = isLoading || waitingOnRetail;
  const dashboardReady = Boolean(
    data && currentView && previousView && !empty && !showLoading
  );

  const handleDownloadPdf = useCallback(async () => {
    const root = exportRootRef.current;
    if (!root || !dashboardReady || isExportingPdf) return;
    setIsExportingPdf(true);
    try {
      await downloadDashboardPdf(root, {
        filename: dashboardPdfFilename(rangeState.current.endDate),
      });
    } catch (err) {
      console.error('Dashboard PDF export failed', err);
      const detail = err instanceof Error && err.message ? ` (${err.message})` : '';
      showToast(`Could not download the dashboard PDF. Please try again.${detail}`, 'error');
    } finally {
      setIsExportingPdf(false);
    }
  }, [dashboardReady, isExportingPdf, rangeState.current.endDate, showToast]);

  if (isConsolidatedDashboardView(searchParams.toString())) {
    return (
      <ConsolidatedDashboard
        profileId={profileId}
        isDemo={isDemo}
        range={compareRange}
        currentView={currentView}
        previousView={previousView}
        snapshotLoading={showLoading}
        snapshotError={error instanceof Error ? error : error ? new Error('Failed to load dashboard') : null}
        onRetry={() => void refetch()}
        truncated={Boolean(data?.truncated)}
        retailDataThrough={retailKpis.dataThrough}
        rangeEnd={rangeState.current.endDate}
      />
    );
  }

  return (
    <div
      ref={exportRootRef}
      className="mx-auto max-w-7xl space-y-5 bg-page px-4 py-6 min-h-full"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="neutral" className="normal-case tracking-normal">
            Ads only · 30d attribution
          </Badge>
          <span className="text-sm text-foreground-muted">Organic vs Paid</span>
          {data?.freshness?.provisionalDates?.length ? (
            <Badge variant="warning" className="normal-case tracking-normal">
              Today/yesterday from hourly stream until Amazon reports import
            </Badge>
          ) : null}
          {retailKpis.dataThrough &&
          rangeState.current.endDate > retailKpis.dataThrough ? (
            <Badge variant="warning" className="normal-case tracking-normal">
              Retail through{' '}
              {new Date(`${retailKpis.dataThrough}T12:00:00`).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}{' '}
              (vendor reports lag 2 days)
            </Badge>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          data-pdf-ignore
          disabled={!dashboardReady || isExportingPdf}
          onClick={() => void handleDownloadPdf()}
          aria-busy={isExportingPdf}
          aria-label={isExportingPdf ? 'Generating PDF' : 'Download dashboard as PDF'}
          className="min-w-[9.75rem] shrink-0 bg-brand text-brand-foreground hover:bg-brand-hover disabled:opacity-80"
        >
          {isExportingPdf ? (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
          ) : (
            <Download className="h-3.5 w-3.5 shrink-0" aria-hidden />
          )}
          <span className="whitespace-nowrap">
            {isExportingPdf ? 'Generating PDF…' : 'Download PDF'}
          </span>
        </Button>
      </div>

      {data?.truncated ? (
        <div
          data-pdf-section
          className="flex items-start gap-2 rounded-xl border border-warning/20 bg-warning-subtle px-4 py-3 text-sm text-warning-ink"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>
            Totals may be incomplete — more than 20,000 report rows in one or both periods. Try a
            shorter date range.
          </p>
        </div>
      ) : null}

      {showLoading ? (
        <div className="flex items-center justify-center gap-2 py-24 text-foreground-muted">
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
          Loading dashboard…
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl border border-error/20 bg-error-subtle px-4 py-6 text-center">
          <p className="text-sm text-error-ink">
            {error instanceof Error ? error.message : 'Failed to load dashboard'}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 text-sm font-semibold text-brand hover:underline"
          >
            Retry
          </button>
        </div>
      ) : null}

      {empty ? (
        <div className="rounded-xl border border-border bg-surface px-4 py-12 text-center">
          <p className="text-foreground-muted">No performance data for this period.</p>
          <SyncReportsButton
            profileId={profileId}
            className="mt-2 text-sm font-semibold text-brand hover:underline"
          />
        </div>
      ) : null}

      {data && currentView && previousView && !empty && !showLoading ? (
        <>
          <div data-pdf-section className="grid gap-4 lg:grid-cols-3">
            <SnapshotGaugeCard
              title="Total Sales"
              variant="sales"
              metric="sales30d"
              currentBuckets={currentView.buckets}
              previousBuckets={previousView.buckets}
              currentTotal={currentView.salesTotal}
              previousTotal={previousView.salesTotal}
              compareEnabled={compareEnabled}
            />
            <SnapshotGaugeCard
              title="Total Orders"
              variant="orders"
              metric="purchases30d"
              currentBuckets={currentView.buckets}
              previousBuckets={previousView.buckets}
              currentTotal={currentView.ordersTotal}
              previousTotal={previousView.ordersTotal}
              compareEnabled={compareEnabled}
            />
            <SnapshotGaugeCard
              title="GVs"
              variant="clicks"
              metric="clicks"
              currentBuckets={currentView.buckets}
              previousBuckets={previousView.buckets}
              currentTotal={currentView.gvTotal}
              previousTotal={previousView.gvTotal}
              compareEnabled={compareEnabled}
            />
          </div>

          <div data-pdf-section className="grid gap-4 lg:grid-cols-3">
            <SnapshotBarCompareCard
              title="Ad AOV"
              mode="aov"
              currentBuckets={currentView.buckets}
              previousBuckets={previousView.buckets}
              currentTotal={currentView.aov}
              previousTotal={previousView.aov}
              compareEnabled={compareEnabled}
            />
            <SnapshotBarCompareCard
              title="Total CR%"
              mode="cr"
              currentBuckets={currentView.buckets}
              previousBuckets={previousView.buckets}
              currentTotal={currentView.cr}
              previousTotal={previousView.cr}
              compareEnabled={compareEnabled}
            />
            <SnapshotRoasCard
              currentBuckets={currentView.buckets}
              previousBuckets={previousView.buckets}
              currentAcos={currentView.acos}
              previousAcos={previousView.acos}
              compareEnabled={compareEnabled}
              currentTaosSpend={currentView.taosSpend}
              previousTaosSpend={previousView.taosSpend}
            />
          </div>

          <div data-pdf-section className="grid items-start gap-4 lg:grid-cols-3">
            <SnapshotSpendHero
              current={data.current.totals.cost}
              previous={data.previous.totals.cost}
              compareEnabled={compareEnabled}
            />
            <div className="lg:col-span-2">
              <SnapshotKpiGrid
                currentTotals={data.current.totals}
                previousTotals={data.previous.totals}
                currentDerived={data.current.derived}
                previousDerived={data.previous.derived}
                compareEnabled={compareEnabled}
              />
            </div>
          </div>

          <ProductReportSection
            profileId={profileId}
            isDemo={isDemo}
            current={compareRange.current}
            previous={compareRange.previous}
          />
        </>
      ) : null}
    </div>
  );
}

